import {
  Users, BedDouble, Car, Ship, MapPin, Ticket, CreditCard, Receipt,
  StickyNote, Accessibility, Mail, Home, Info, AlertTriangle, ShieldQuestion, IdCard,
} from "lucide-react";
import { Badge } from "./ui/badge";

const paxTypeClass = {
  CHD: "bg-amber-100 text-amber-800 border-amber-200",
  INF: "bg-pink-100 text-pink-800 border-pink-200",
  ADT: "bg-slate-100 text-slate-700 border-slate-200",
};

function Section({ icon: Icon, title, count, dark, children, testid }) {
  return (
    <div data-testid={testid} className={`rounded-lg border p-3.5 ${dark ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-white"}`}>
      <div className="flex items-center gap-2 mb-2.5">
        <Icon className={`h-4 w-4 ${dark ? "text-blue-400" : "text-blue-600"}`} />
        <h3 className={`font-heading font-bold text-sm ${dark ? "text-slate-100" : "text-slate-900"}`}>{title}</h3>
        {count != null && (
          <span className={`text-xs font-pnr px-1.5 py-0.5 rounded ${dark ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-500"}`}>{count}</span>
        )}
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Field({ label, value, dark }) {
  if (value == null || value === "" ) return null;
  return (
    <div className="flex items-baseline gap-2 text-sm">
      <span className={`shrink-0 text-xs uppercase tracking-wide ${dark ? "text-slate-500" : "text-slate-400"}`}>{label}</span>
      <span className={`${dark ? "text-slate-200" : "text-slate-700"} break-words`}>{value}</span>
    </div>
  );
}

const money = (cur, amt) => (amt ? `${cur ? cur + " " : ""}${amt}` : null);

export default function ModelSummary({ model, dark = false }) {
  if (!model) return null;
  const m = model;
  const p = m.pnr || {};
  const t = m.ticketing || {};
  const fare = (m.fares && m.fares[0]) || null;
  const hasHeader = p.recordLocator || p.officeId || p.creationDate || p.receivedFrom;

  return (
    <div className="space-y-3 mt-3" data-testid="model-summary">
      {hasHeader && (
        <Section icon={Info} title="Booking" dark={dark} testid="model-pnr">
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
            <Field label="Record locator" value={p.recordLocator} dark={dark} />
            <Field label="Office ID" value={p.officeId} dark={dark} />
            <Field label="Created" value={p.creationDate} dark={dark} />
            <Field label="Received from" value={p.receivedFrom} dark={dark} />
          </div>
        </Section>
      )}

      {m.passengers?.length > 0 && (
        <Section icon={Users} title="Passengers" count={m.passengers.length} dark={dark} testid="model-passengers">
          {m.passengers.map((pax, i) => (
            <div key={`pax-${i}`} data-testid="model-passenger" className="flex items-center justify-between text-sm">
              <span className={dark ? "text-slate-200" : "text-slate-700"}>
                {[pax.title, pax.firstName, pax.lastName].filter(Boolean).join(" ")}
                <span className={`ml-2 font-pnr text-xs ${dark ? "text-slate-500" : "text-slate-400"}`}>#{pax.id}</span>
              </span>
              <Badge variant="outline" className={`rounded-full text-xs ${paxTypeClass[pax.passengerType] || paxTypeClass.ADT}`}>
                {pax.passengerTypeName || pax.passengerType}
              </Badge>
            </div>
          ))}
        </Section>
      )}

      {m.hotels?.length > 0 && (
        <Section icon={BedDouble} title="Hotels" count={m.hotels.length} dark={dark} testid="model-hotels">
          {m.hotels.map((h, i) => (
            <div key={`htl-${i}`} data-testid="model-hotel" className={`rounded-md p-2 ${dark ? "bg-slate-900/50" : "bg-slate-50"}`}>
              <div className={`font-medium text-sm ${dark ? "text-slate-100" : "text-slate-900"}`}>{h.hotelName || "Hotel"}{h.city ? ` · ${h.city}` : ""}</div>
              <Field label="Stay" value={h.checkIn ? `${h.checkIn} → ${h.checkOut || ""}` : null} dark={dark} />
              <Field label="Room" value={h.roomType} dark={dark} />
              <Field label="Rate" value={money(h.currency, h.rate)} dark={dark} />
              <Field label="Conf" value={h.confirmationNumber} dark={dark} />
              <Field label="Address" value={h.address} dark={dark} />
            </div>
          ))}
        </Section>
      )}

      {m.cars?.length > 0 && (
        <Section icon={Car} title="Car Rentals" count={m.cars.length} dark={dark} testid="model-cars">
          {m.cars.map((c, i) => (
            <div key={`car-${i}`} data-testid="model-car" className={`rounded-md p-2 ${dark ? "bg-slate-900/50" : "bg-slate-50"}`}>
              <div className={`font-medium text-sm ${dark ? "text-slate-100" : "text-slate-900"}`}>{c.supplier || "Car"}{c.vehicleCategory ? ` · ${c.vehicleCategory}` : ""}{c.transmission ? ` (${c.transmission})` : ""}</div>
              <Field label="Pickup" value={c.pickupDate ? `${c.pickupDate} ${c.pickupTime || ""} ${c.pickupLocation || ""}`.trim() : null} dark={dark} />
              <Field label="Dropoff" value={c.dropoffDate ? `${c.dropoffDate} ${c.dropoffTime || ""} ${c.dropoffLocation || ""}`.trim() : null} dark={dark} />
              <Field label="Rate" value={money(c.currency, c.rate)} dark={dark} />
              <Field label="Conf" value={c.confirmationNumber} dark={dark} />
            </div>
          ))}
        </Section>
      )}

      {m.cruises?.length > 0 && (
        <Section icon={Ship} title="Cruises" count={m.cruises.length} dark={dark} testid="model-cruises">
          {m.cruises.map((c, i) => (
            <div key={`cru-${i}`} data-testid="model-cruise" className={`rounded-md p-2 ${dark ? "bg-slate-900/50" : "bg-slate-50"}`}>
              <div className={`font-medium text-sm ${dark ? "text-slate-100" : "text-slate-900"}`}>{c.shipName || "Cruise"}</div>
              <Field label="Sailing" value={c.sailingStart ? `${c.sailingStart} → ${c.sailingEnd || ""}` : null} dark={dark} />
              <Field label="Cabin" value={c.cabin} dark={dark} />
              <Field label="Category" value={c.category} dark={dark} />
              <Field label="Conf" value={c.confirmationNumber} dark={dark} />
            </div>
          ))}
        </Section>
      )}

      {(m.tours?.length > 0 || m.transfers?.length > 0) && (
        <Section icon={MapPin} title="Tours, Excursions & Transfers" count={(m.tours?.length || 0) + (m.transfers?.length || 0)} dark={dark} testid="model-tours">
          {(m.tours || []).map((tr, i) => (
            <div key={`tour-${i}`} data-testid="model-tour" className={`rounded-md p-2 ${dark ? "bg-slate-900/50" : "bg-slate-50"}`}>
              <div className={`font-medium text-sm ${dark ? "text-slate-100" : "text-slate-900"}`}>{tr.name || "Activity"}{tr.city ? ` · ${tr.city}` : ""}</div>
              <Field label="When" value={[tr.date, tr.timeFrom ? `${tr.timeFrom}-${tr.timeTo || ""}` : tr.duration].filter(Boolean).join(" ")} dark={dark} />
              <Field label="Pickup" value={tr.pickup} dark={dark} />
              <Field label="Conf" value={tr.confirmationNumber} dark={dark} />
            </div>
          ))}
          {(m.transfers || []).map((tr, i) => (
            <div key={`trf-${i}`} data-testid="model-transfer" className={`rounded-md p-2 ${dark ? "bg-slate-900/50" : "bg-slate-50"}`}>
              <div className={`font-medium text-sm ${dark ? "text-slate-100" : "text-slate-900"}`}>{tr.detail || "Transfer"}{tr.city ? ` · ${tr.city}` : ""}</div>
              <Field label="Date" value={tr.date} dark={dark} />
              <Field label="Conf" value={tr.confirmationNumber} dark={dark} />
            </div>
          ))}
        </Section>
      )}

      {(m.tickets?.length > 0 || m.emds?.length > 0 || t.ticketTimeLimit || t.validatingCarrier || m.payments?.length > 0 || fare || t.endorsement || t.nonRefundable) && (
        <Section icon={Ticket} title="Ticketing & Payment" dark={dark} testid="model-ticketing">
          {m.tickets?.length > 0 && (
            <Field label="Tickets" value={m.tickets.map((x) => x.ticketNumber).filter(Boolean).join(", ")} dark={dark} />
          )}
          {m.emds?.length > 0 && (
            <Field label="EMD" value={m.emds.map((x) => x.emdNumber).filter(Boolean).join(", ")} dark={dark} />
          )}
          <Field label="Ticket time limit" value={t.ticketTimeLimit} dark={dark} />
          <Field label="Validating carrier" value={t.validatingCarrier} dark={dark} />
          {m.payments?.filter((x) => x.maskedNumber || x.method).map((pm, i) => (
            <Field key={`pay-${i}`} label="Form of payment" value={[pm.maskedNumber || pm.method, pm.expiry ? `exp ${pm.expiry}` : null].filter(Boolean).join(" · ")} dark={dark} />
          ))}
          <Field label="Endorsement" value={t.endorsement} dark={dark} />
          {t.nonRefundable && <Field label="Fare rule" value="Non-refundable" dark={dark} />}
          {fare && (
            <div className="pt-1.5 mt-1 border-t border-dashed border-slate-200/60">
              <Field label="Base fare" value={money(fare.currency, fare.baseFare)} dark={dark} />
              <Field label="Taxes" value={money(fare.currency, fare.taxes)} dark={dark} />
              <Field label="Total" value={money(fare.currency, fare.total)} dark={dark} />
            </div>
          )}
        </Section>
      )}

      {m.ssr?.length > 0 && (
        <Section icon={Accessibility} title="Special Services (SSR)" count={m.ssr.length} dark={dark} testid="model-ssr">
          {m.ssr.map((s, i) => (
            <div key={`ssr-${i}`} data-testid="model-ssr-item" className="flex items-start gap-2 text-sm">
              <Badge variant="outline" className={`rounded-full text-xs font-pnr shrink-0 ${s.recognized ? (dark ? "border-slate-600 text-slate-300" : "border-slate-200 text-slate-600") : "border-amber-300 text-amber-700 bg-amber-50"}`}>
                {s.code}
              </Badge>
              <span className={dark ? "text-slate-300" : "text-slate-700"}>
                {s.document ? (
                  <span className="inline-flex items-center gap-1.5"><IdCard className="h-3.5 w-3.5 text-slate-400" />{[s.document.docType === "P" ? "Passport" : s.document.docType, s.document.number, s.document.nationality, s.document.lastName && `${s.document.lastName}/${s.document.firstName || ""}`, s.document.expiry && `exp ${s.document.expiry}`].filter(Boolean).join(" · ")}</span>
                ) : (
                  <>{s.description}{s.airline ? ` (${s.airline})` : ""}{s.frequentFlyer ? ` · ${s.frequentFlyer}` : ""}</>
                )}
              </span>
            </div>
          ))}
        </Section>
      )}

      {(m.contacts?.length > 0 || m.addresses?.length > 0) && (
        <Section icon={Mail} title="Contacts & Address" dark={dark} testid="model-contacts">
          {m.contacts.map((c, i) => (
            <Field key={`ct-${i}`} label={c.kind} value={c.email || c.phone} dark={dark} />
          ))}
          {m.addresses.map((a, i) => (
            <div key={`ad-${i}`} className="flex items-baseline gap-2 text-sm">
              <span className={`shrink-0 text-xs uppercase tracking-wide ${dark ? "text-slate-500" : "text-slate-400"}`}><Home className="h-3.5 w-3.5 inline" /> {a.kind}</span>
              <span className={dark ? "text-slate-200" : "text-slate-700"}>{a.text}</span>
            </div>
          ))}
        </Section>
      )}

      {m.osi?.length > 0 && (
        <Section icon={StickyNote} title="Other Service Info (OSI)" count={m.osi.length} dark={dark} testid="model-osi">
          {m.osi.map((o, i) => (
            <div key={`osi-${i}`} className={`text-sm ${dark ? "text-slate-300" : "text-slate-700"}`}>{o.carrier ? <span className="font-pnr text-xs text-slate-400 mr-1.5">{o.carrier}</span> : null}{o.text}</div>
          ))}
        </Section>
      )}

      {m.remarks?.length > 0 && (
        <Section icon={Receipt} title="Remarks" count={m.remarks.length} dark={dark} testid="model-remarks">
          {m.remarks.map((r, i) => (
            <div key={`rm-${i}`} className={`text-sm ${dark ? "text-slate-300" : "text-slate-700"}`}>
              {r.qualifier ? <span className="font-pnr text-xs text-slate-400 mr-1.5">{r.qualifier}</span> : null}{r.text}
            </div>
          ))}
        </Section>
      )}

      {m.errors?.length > 0 && (
        <div data-testid="model-errors" className={`rounded-lg border p-3.5 ${dark ? "border-amber-800/60 bg-amber-950/30" : "border-amber-200 bg-amber-50"}`}>
          <div className="flex items-center gap-2 mb-1">
            <ShieldQuestion className="h-4 w-4 text-amber-500" />
            <h3 className={`font-heading font-bold text-sm ${dark ? "text-amber-200" : "text-amber-800"}`}>Lines we couldn't decode</h3>
            <span className="text-xs font-pnr px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{m.errors.length}</span>
          </div>
          <p className={`text-xs mb-2 ${dark ? "text-amber-300/70" : "text-amber-700/80"}`}>These lines were kept exactly as received but didn't match a known GDS element.</p>
          <ul className="space-y-1">
            {m.errors.map((e, i) => (
              <li key={`err-${i}`} data-testid="model-error-item" className={`font-pnr text-xs rounded px-2 py-1 break-words ${dark ? "bg-slate-900 text-amber-200" : "bg-white text-amber-800 border border-amber-100"}`}>
                <span className="text-slate-400 mr-2">L{e.lineNumber}</span>{e.raw}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
